import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Textarea, Text, Stack, Collapse, Tabs } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faWandMagicSparkles, 
  faPlay, 
  faPause,
  faVolumeHigh,
  faDownload,
  faRefresh,
  faCheck,
  faChevronDown,
  faChevronUp,
  faLightbulb,
  faMicrophone
} from '@fortawesome/free-solid-svg-icons';
import { useNotification } from '../../context/NotificationContext';
import { projectService, PromptProject } from '../../services/projectService';
import { PromptBrowser } from '../shared';
import { getApiUrl } from '../../config/api';
import styles from './AudioModal.module.css'; // Reuse existing styles
import CustomSlider from './CustomSlider';

interface SoundEffectsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSoundEffectGenerated: (soundEffectUrl: string, filename: string, duration: number) => void;
  userId: string;
  currentProjectId?: string;
}

interface GenerateEnvironmentalSoundRequest {
  prompt: string;
  duration?: number;
  temperature?: number;
  top_k?: number;
  top_p?: number;
  num_samples?: number;
  userId: string;
}

interface GenerateEnvironmentalSoundResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    prompt: string;
    soundEffects: Array<{
      index: number;
      s3Url: string;
      duration: number;
      filename: string;
    }>;
    count: number;
  };
}

// Popular sound effect prompts for quick selection (using translation keys)
const POPULAR_PROMPTS = [
  { key: "gentleRain", icon: "🌧️", category: "Nature" },
  { key: "oceanWaves", icon: "🌊", category: "Nature" },
  { key: "campfire", icon: "🔥", category: "Nature" },
  { key: "cityStreet", icon: "🏙️", category: "Urban" },
  { key: "thunderstorm", icon: "⛈️", category: "Nature" },
  { key: "birdsChirping", icon: "🐦", category: "Nature" },
  { key: "coffeeShop", icon: "☕", category: "Indoor" },
  { key: "typing", icon: "⌨️", category: "Office" },
  { key: "footsteps", icon: "👣", category: "Movement" },
  { key: "clockTicking", icon: "🕐", category: "Indoor" },
  { key: "windTrees", icon: "🌳", category: "Nature" },
  { key: "churchBells", icon: "🔔", category: "Urban" }
];

// Temperature options for sound generation
const TEMPERATURE_OPTIONS = [
  { value: 0.5, label: 'Focused', description: 'More predictable and coherent sounds' },
  { value: 1.0, label: 'Balanced', description: 'Good balance of creativity and coherence' },
  { value: 1.5, label: 'Creative', description: 'More variation and unexpected elements' },
  { value: 2.0, label: 'Experimental', description: 'Maximum creativity and variation' },
];

// Custom Select Component
interface CustomSelectProps {
  data: Array<{ value: string; label: string; description?: string }>;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({ data, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  
  const selectedItem = data.find(item => item.value === value);
  const filteredData = data.filter(item => 
    item.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.customSelect} ref={selectRef}>
      <div 
        className={styles.selectTrigger} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={styles.selectValue}>
          {selectedItem ? selectedItem.label : placeholder || 'Select an option'}
        </span>
        <FontAwesomeIcon 
          icon={faChevronDown} 
          className={`${styles.selectArrow} ${isOpen ? styles.selectArrowOpen : ''}`}
        />
      </div>
      
      {isOpen && (
        <div className={styles.selectDropdown}>
          {data.length > 5 && (
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={styles.selectSearch}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className={styles.selectOptions}>
            {filteredData.map((item) => (
              <div
                key={item.value}
                className={`${styles.selectOption} ${item.value === value ? styles.selectOptionSelected : ''}`}
                onClick={() => handleSelect(item.value)}
              >
                <div className={styles.selectOptionLabel}>{item.label}</div>
                {item.description && (
                  <div className={styles.selectOptionDescription}>{item.description}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SoundEffectsModal: React.FC<SoundEffectsModalProps> = ({ 
  isOpen, 
  onClose, 
  onSoundEffectGenerated,
  userId,
  currentProjectId
}) => {
  const { t } = useTranslation('soundEditor');
  
  const [activeStep, setActiveStep] = useState<'generation' | 'result'>('generation');
  const [activeTab, setActiveTab] = useState<'generation' | 'prompts'>('generation');
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Form state
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(12.0); // Match the default shown in UI
  const [temperature, setTemperature] = useState(1.0);
  const [topK, setTopK] = useState(250);
  const [topP, setTopP] = useState(0.0);
  const [numSamples, setNumSamples] = useState(1);
  
  // Result state
  const [generatedSoundEffects, setGeneratedSoundEffects] = useState<Array<{
    index: number;
    s3Url: string;
    duration: number;
    filename: string;
  }>>([]);
  const [selectedSoundEffectIndex, setSelectedSoundEffectIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  
  // Accordion state
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  
  const { showNotification } = useNotification();

  // Reset form when modal closes
  const handleClose = () => {
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setIsPlaying(false);
    setActiveStep('generation');
    setPrompt('');
    setGeneratedSoundEffects([]);
    onClose();
  };

  // Generate environmental sound
  const generateEnvironmentalSound = async (): Promise<GenerateEnvironmentalSoundResponse> => {
    const request: GenerateEnvironmentalSoundRequest = {
      prompt: prompt,
      duration: duration,
      temperature: temperature,
      top_k: topK,
      top_p: topP,
      num_samples: numSamples,
      // 🔒 NOTE: userId included for backward compatibility but backend uses authenticated user ID
      userId: userId,
    };

    const response = await fetch(getApiUrl('/music-generation/environmental-sound'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  };

  // Handle generation
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      showNotification({
        title: 'Missing Description',
        message: 'Please enter a description for the sound effect',
        type: 'error'
      });
      return;
    }

    if (!userId) {
      showNotification({
        title: 'Authentication Required',
        message: 'Please log in to generate sound effects',
        type: 'error'
      });
      return;
    }

    setIsGenerating(true);

    try {
      const response = await generateEnvironmentalSound();
      if (response.success) {
        // Handle the response structure from the backend
        const soundEffects = response.data.soundEffects || [];
        setGeneratedSoundEffects(soundEffects);
        setActiveStep('result');
        // showNotification({
        //   title: 'Success',
        //   message: `Generated ${soundEffects.length} sound effect(s) successfully!`,
        //   type: 'success'
        // });
      }
    } catch (error) {
      console.error('Generation failed:', error);
      showNotification({
        title: 'Generation Failed',
        message: 'Failed to generate sound effect. Please try again.',
        type: 'error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle audio playback
  const handlePlayPause = (audioUrl: string) => {
    if (currentAudio && !currentAudio.paused) {
      currentAudio.pause();
      setIsPlaying(false);
      setCurrentAudio(null);
    } else {
      if (currentAudio) {
        currentAudio.pause();
      }
      
      const audio = new Audio(audioUrl);
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentAudio(null);
      });
      
      audio.play();
      setIsPlaying(true);
      setCurrentAudio(audio);
    }
  };

  // Handle regeneration
  const handleRegenerate = () => {
    setActiveStep('generation');
    setGeneratedSoundEffects([]);
    if (currentAudio) {
      currentAudio.pause();
      setCurrentAudio(null);
    }
    setIsPlaying(false);
  };

  // Handle completion - adds to timeline and refreshes sound effects list
  const handleComplete = () => {
    const selectedSoundEffect = generatedSoundEffects[selectedSoundEffectIndex];
    if (selectedSoundEffect) {
      onSoundEffectGenerated(
        selectedSoundEffect.s3Url, 
        selectedSoundEffect.filename, 
        selectedSoundEffect.duration
      );
      handleClose();
    }
  };

  // Handle popular prompt selection
  const handlePopularPromptSelect = (promptKey: string) => {
    const translatedPrompt = t(`soundEffectsModal.prompts.${promptKey}`);
    setPrompt(translatedPrompt);
  };

  // Handle using prompt for sound effect generation
  const handleLoadPromptForAudio = React.useCallback((promptText: string, project: PromptProject) => {
    setPrompt(promptText);
    setActiveTab('generation');
    
    // showNotification({
    //   title: 'Prompt Loaded',
    //   message: 'Prompt loaded for sound effect generation',
    //   type: 'success'
    // });
  }, [setPrompt, setActiveTab, showNotification]);

  // Handle using prompt (backward compatibility)
  const handleUsePrompt = (promptText: string, project: PromptProject) => {
    setPrompt(promptText);
    setActiveTab('generation');
    // showNotification({
    //   title: 'Prompt Applied',
    //   message: 'Prompt has been applied to sound effect generation',
    //   type: 'success'
    // });
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {activeStep === 'generation' ? t('soundEffectsModal.title') : t('soundEffectsModal.resultTitle')}
          </h2>
          <button className={styles.closeButton} onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {activeStep === 'generation' ? (
          <>
            <Tabs value={activeTab} onChange={(value) => setActiveTab(value as 'generation' | 'prompts')}>
              <Tabs.List>
                <Tabs.Tab value="generation">
                  <FontAwesomeIcon icon={faMicrophone} style={{ marginRight: '8px' }} />
                  {t('soundEffectsModal.tabs.generation')}
                </Tabs.Tab>
                <Tabs.Tab value="prompts">
                  <FontAwesomeIcon icon={faLightbulb} style={{ marginRight: '8px' }} />
                  {t('soundEffectsModal.tabs.prompts')}
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="generation" pt="md">
                <div className={styles.generationContent}>
                  <Stack gap="xl">
              <div>
                <Text size="sm" fw={500} mb="xs">{t('soundEffectsModal.descriptionLabel')}</Text>
                <Textarea
                  placeholder={t('soundEffectsModal.descriptionPlaceholder')}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  minRows={3}
                  maxRows={6}
                />
              </div>

              {/* Popular Prompts */}
              <div>
                <Text size="sm" fw={500} mb="xs">{t('soundEffectsModal.popularEffects')}</Text>
                <div className={styles.voiceTemplates}>
                  {POPULAR_PROMPTS.map((item, index) => {
                    const translatedPrompt = t(`soundEffectsModal.prompts.${item.key}`);
                    return (
                    <div
                      key={index}
                      className={`${styles.voiceTemplate} ${
                        prompt === translatedPrompt ? styles.voiceTemplateSelected : ''
                      }`}
                      onClick={() => handlePopularPromptSelect(item.key)}
                    >
                      <div className={styles.voiceTemplateContent}>
                        <div className={styles.voiceTemplateLabel}>
                          {item.icon} {translatedPrompt}
                        </div>
                        <div className={styles.voiceTemplateTone}>
                          {t(`soundEffectsModal.categories.${item.category.toLowerCase()}`)}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              <CustomSlider
                value={duration}
                onChange={setDuration}
                min={1.0}
                max={30.0}
                step={0.5}
                marks={[
                  { value: 5, label: '5s' },
                  { value: 10, label: '10s' },
                  { value: 15, label: '15s' },
                  { value: 30, label: '30s' }
                ]}
                label={t('soundEffectsModal.durationLabel')}
                description={t('soundEffectsModal.durationCurrent', { duration })}
              />

              {/* Advanced Configuration Accordion */}
              <div className={styles.accordionContainer}>
                <button
                  className={styles.accordionTrigger}
                  onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                  type="button"
                >
                  <span className={styles.accordionTitle}>{t('soundEffectsModal.advanced.title')}</span>
                  <FontAwesomeIcon 
                    icon={isAdvancedOpen ? faChevronUp : faChevronDown} 
                    className={styles.accordionArrow}
                  />
                </button>
                
                <Collapse in={isAdvancedOpen}>
                  <div className={styles.accordionContent}>
                    <Stack gap="md" pt="md">
                      <div>
                        <Text size="sm" fw={500} mb="xs">{t('soundEffectsModal.advanced.creativityLabel')}</Text>
                        <CustomSelect
                          data={TEMPERATURE_OPTIONS.map(temp => ({
                            value: temp.value.toString(),
                            label: t(`soundEffectsModal.advanced.creativity.${temp.label.toLowerCase()}`),
                            description: t(`soundEffectsModal.advanced.creativityDesc.${temp.label.toLowerCase()}`)
                          }))}
                          value={temperature.toString()}
                          onChange={(value) => setTemperature(parseFloat(value))}
                          placeholder={t('soundEffectsModal.advanced.creativityPlaceholder')}
                        />
                        <Text size="xs" color="dimmed" mt="xs">
                          {t(`soundEffectsModal.advanced.creativityDesc.${TEMPERATURE_OPTIONS.find(t => t.value === temperature)?.label.toLowerCase()}`)}
                        </Text>
                      </div>

                      <CustomSlider
                        value={topK}
                        onChange={setTopK}
                        min={1}
                        max={1000}
                        step={10}
                        marks={[
                          { value: 50, label: t('soundEffectsModal.advanced.diversity.focused') },
                          { value: 250, label: t('soundEffectsModal.advanced.diversity.balanced') },
                          { value: 500, label: t('soundEffectsModal.advanced.diversity.diverse') },
                          { value: 1000, label: t('soundEffectsModal.advanced.diversity.veryDiverse') }
                        ]}
                        label={t('soundEffectsModal.advanced.diversityLabel')}
                        description={t('soundEffectsModal.advanced.diversityCurrent', { topK })}
                      />

                      <CustomSlider
                        value={numSamples}
                        onChange={setNumSamples}
                        min={1}
                        max={3}
                        step={1}
                        marks={[
                          { value: 1, label: t('soundEffectsModal.advanced.samples.one') },
                          { value: 2, label: t('soundEffectsModal.advanced.samples.two') },
                          { value: 3, label: t('soundEffectsModal.advanced.samples.three') }
                        ]}
                        label={t('soundEffectsModal.advanced.variationsLabel')}
                        description={t('soundEffectsModal.advanced.variationsDesc', { numSamples })}
                      />
                    </Stack>
                  </div>
                </Collapse>
              </div>
            </Stack>
          </div>
              </Tabs.Panel>

              <Tabs.Panel value="prompts" pt="md">
                <Stack gap="md" style={{ minHeight: '400px' }}>
                  <div>
                    <Text size="sm" fw={500} mb="xs">{t('soundEffectsModal.promptsTab.title')}</Text>
                    <Text size="xs" color="dimmed" mb="md">
                      {t('soundEffectsModal.promptsTab.description')}
                    </Text>
                  </div>

                  <PromptBrowser
                    onPromptSelect={handleUsePrompt}
                    onLoadForSound={handleLoadPromptForAudio}
                    showUseActions={true}
                    showSelectAction={false}
                    compact={true}
                    className={styles.modalPromptBrowser}
                    projectId={currentProjectId}
                    height="350px"
                  />
                  
                  <div style={{ 
                    backgroundColor: 'rgba(59, 130, 246, 0.1)', 
                    padding: '12px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(59, 130, 246, 0.2)'
                  }}>
                    <Text size="xs" color="dimmed">
                      {t('soundEffectsModal.promptsTab.tip')}
                    </Text>
                  </div>
                </Stack>
              </Tabs.Panel>
            </Tabs>

            <div className={styles.modalActions}>
              <button onClick={handleClose} className={styles.cancelButton}>
                {t('soundEffectsModal.cancelButton')}
              </button>
              {activeTab !== 'prompts' && (
                <Button 
                  onClick={handleGenerate} 
                  loading={isGenerating}
                  className={styles.primaryButton}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} style={{marginRight: "8px"}} />
                  {isGenerating ? t('soundEffectsModal.generating') : t('soundEffectsModal.generateButton')}
                </Button>
              )}
            </div>
          </>
        ) : (
          // Result step
          <div className={styles.resultSection}>
            {generatedSoundEffects.length > 0 ? (
              <div className={styles.melodyResults}>
                <h3>{t('soundEffectsModal.results.title')}</h3>
                <p>{t('soundEffectsModal.results.description', { count: generatedSoundEffects.length })}</p>
                
                <div className={styles.melodyOptions}>
                  {generatedSoundEffects.map((soundEffect, index) => (
                    <div 
                      key={soundEffect.index}
                      className={`${styles.melodyOption} ${selectedSoundEffectIndex === index ? styles.selected : ''}`}
                      onClick={() => setSelectedSoundEffectIndex(index)}
                    >
                      <div className={styles.melodyHeader}>
                        <h4>Sound Effect {soundEffect.index}</h4>
                        <span className={styles.duration}>{soundEffect.duration.toFixed(1)}s</span>
                      </div>
                      <div className={styles.melodyControls}>
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlayPause(soundEffect.s3Url);
                          }}
                          leftSection={<FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />}
                        >
                          {isPlaying ? 'Pause' : 'Play'}
                        </Button>
                        <Button
                          size="sm"
                          component="a"
                          href={soundEffect.s3Url}
                          download={soundEffect.filename}
                          variant="outline"
                          leftSection={<FontAwesomeIcon icon={faDownload} />}
                        >
                          Download
                        </Button>
                      </div>
                      {selectedSoundEffectIndex === index && (
                        <div className={styles.selectedIndicator}>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className={styles.modalActions}>
              <button 
                onClick={handleRegenerate}
                className={styles.cancelButton}
              >
                <FontAwesomeIcon icon={faRefresh} style={{marginRight: "8px"}} />
                {t('soundEffectsModal.regenerateButton')}
              </button>
              <Button 
                onClick={handleComplete}
                className={styles.primaryButton}
              >
                <FontAwesomeIcon icon={faCheck} style={{marginRight: "8px"}} />
                {t('soundEffectsModal.addToTimelineButton')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoundEffectsModal;